import { User } from 'lucide-react';
import { toast } from 'sonner';
import { useState, useEffect, lazy, Suspense } from 'react';
import { CARD_OVERLAY_GRADIENT, CARD_SHADOW, CARD_BORDER_RADIUS } from '../constants/cardStyles';
// Lazy-loaded to break the ProductCard <-> ProductModal circular import.
const ProductModal = lazy(() => import('./ProductModal'));
import { trackProductView, trackProductClick } from '@/utils/analytics';
import { API_STRING as api } from '@/App';
import { useDarkMode } from '@/hooks/useDarkMode';
import useScrollLock from '@/hooks/useScrollLock';
import { proxiedImage } from '@/utils/proxiedImage';

import { logger } from '@/utils/logger';
import { Heart } from 'lucide-react';
import RecipientSelectorModal from '@/components/RecipientSelectorModal';
import WishlistSelectorModal from '@/components/WishlistSelectorModal';
import ImageCarousel from '@/components/ImageCarousel';
export default function ProductCard({ product, user, onSaveClick, source = 'browse', isInWishlist = false, onWishlistChange, hideHeartButton = false, className = '', aiMetadata, onQuickView }) {
  const darkMode = useDarkMode();
  const [showModal, setShowModal] = useState(false);
  const [inWishlist, setInWishlist] = useState(isInWishlist);
  const [savingWishlist, setSavingWishlist] = useState(false);
  const [showRecipientSelector, setShowRecipientSelector] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showWishlistSelector, setShowWishlistSelector] = useState(false);
  useScrollLock(showSaveModal);
  
  // Get images array - prefer the canonical `image_url` as the lead image
  // (the Unsplash photos in `images[]` were seeded with shared placeholders so
  // multiple bundled products were showing the same thumbnail). When both are
  // present, the carousel still gets the full set, but the unique image_url
  // is shown first. All URLs are routed through the backend image proxy
  // so blocked CDN hosts (e.g. images.unsplash.com on some networks) still
  // resolve via our own domain.
  const productImages = (() => {
    let urls;
    if (product.image_url && product.images && product.images.length > 0) {
      const rest = product.images.filter((u) => u !== product.image_url);
      urls = [product.image_url, ...rest];
    } else if (product.images && product.images.length > 0) {
      urls = product.images;
    } else {
      urls = product.image_url ? [product.image_url] : [];
    }
    return urls.map(proxiedImage);
  })();
  
  useEffect(() => {
    setInWishlist(isInWishlist);
  }, [isInWishlist]);
  
  const handleWishlistToggle = async (e) => {
    e.stopPropagation();
    
    if (!user) {
      window.dispatchEvent(new CustomEvent('mementa:open-auth'));
      toast.info('Sign in to save gifts');
      return;
    }
    
    setSavingWishlist(true);
    try {
      const method = inWishlist ? 'DELETE' : 'POST';
      const response = await fetch(`${api}/wishlist/${product.id}`, {
        method,
      });
      
      if (response.ok) {
        setInWishlist(!inWishlist);
        toast.success(inWishlist ? 'Removed from wishlist' : 'Added to wishlist!');
        if (onWishlistChange) {
          onWishlistChange(product.id, !inWishlist);
        }
      } else {
        throw new Error('Failed to update wishlist');
      }
    } catch (error) {
      toast.error('Failed to update wishlist');
      logger.error(error);
    } finally {
      setSavingWishlist(false);
    }
  };

  const handleSave = () => {
    if (!user) {
      window.dispatchEvent(new CustomEvent('mementa:open-auth'));
      toast.info('Sign in to save gifts');
      return;
    }
    setShowRecipientSelector(true);
  };

  const handleCardClick = () => {
    trackProductView(product.id, source);
    if (onQuickView) {
      onQuickView(product);
      return;
    }
    setShowModal(true);
  };

  const handleMerchantClick = (e) => {
    e.stopPropagation();
    trackProductClick(product.id, source);
  };

  return (
    <>
      <div
        data-testid={`product-card-${product.id}`}
        className={`product-card relative ${CARD_BORDER_RADIUS} overflow-hidden group h-[280px] sm:h-[350px] lg:h-[420px] transition-shadow duration-300 cursor-pointer ${className}`}
        style={{ boxShadow: CARD_SHADOW }}
        onClick={handleCardClick}
      >
      {/* Heart Button - Top Right (guests get the sign-in prompt on click) */}
      {!hideHeartButton && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (!user) {
              window.dispatchEvent(new CustomEvent('mementa:open-auth'));
              toast.info('Sign in to save gifts');
              return;
            }
            setShowSaveModal(true);
          }}
          data-testid={`product-heart-btn-${product.id}`}
          className="absolute top-3 right-3 z-10 p-2 rounded-full transition-transform"
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          <Heart 
            className="w-5 h-5" 
            style={{ 
              color: inWishlist ? '#9E6752' : '#534145',
              fill: inWishlist ? '#9E6752' : 'none'
            }} 
          />
        </button>
      )}
      
      {/* Out of stock badge - Top Left */}
      {product.stock_status === 'out_of_stock' && (
        <div
          data-testid={`product-oos-badge-${product.id}`}
          className="absolute top-3 left-3 z-10 px-3 py-1 rounded-full text-xs font-semibold"
          style={{ background: 'rgba(83, 65, 69, 0.92)', color: '#FED7A5', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
        >
          Out of stock
        </div>
      )}

      {/* Image Carousel - Full card */}
      <ImageCarousel 
        images={productImages}
        alt={product.name}
        compact={true}
        showIndicators={false}
      />
      
      {/* Overlay with details - image-forward: just name, price, merchant.
          Full details (description, tags, checkout info) live in the modal.
          Colors are pinned since the gradient is always white in both themes. */}
      <div className="absolute bottom-0 left-0 right-0 w-full px-3 sm:px-4 pb-3 sm:pb-4 pt-6 sm:pt-8" style={{background: CARD_OVERLAY_GRADIENT}}>
        <h3 className="text-sm sm:text-base mb-1 font-semibold leading-snug line-clamp-2" style={{fontFamily: 'Playfair Display, serif', color: '#20212B'}}>
          {product.name}
        </h3>

        <div className="flex justify-between items-center gap-2">
          <span className="text-lg sm:text-xl font-bold" style={{color: '#9E6752'}}>
            ${typeof product.price === 'number' ? product.price.toFixed(2) : parseFloat(product.price || 0).toFixed(2)}
          </span>
          {product.merchant && (
            <span className="text-[10px] sm:text-xs font-medium truncate" style={{color: '#73766A'}} data-testid={`product-merchant-${product.id}`}>
              {product.merchant}
            </span>
          )}
        </div>
      </div>
      </div>

      {/* Product Modal (lazy-loaded to break circular import) */}
      {showModal && (
        <Suspense fallback={null}>
          <ProductModal 
            product={product} 
            onClose={() => setShowModal(false)} 
            user={user}
            aiMetadata={aiMetadata}
          />
        </Suspense>
      )}

      {/* Recipient Selector Modal */}
      {showRecipientSelector && (
        <RecipientSelectorModal
          product={product}
          user={user}
          onClose={() => setShowRecipientSelector(false)}
        />
      )}

      {/* Wishlist Selector Modal */}
      {showWishlistSelector && (
        <WishlistSelectorModal
          isOpen={showWishlistSelector}
          onClose={() => setShowWishlistSelector(false)}
          productId={product.id}
          onSuccess={() => {
            setShowWishlistSelector(false);
            // Toast is already shown in WishlistSelectorModal
          }}
        />
      )}

      {/* Save Modal - Choose Wishlist or Keepsake */}
      {showSaveModal && (
        <div 
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
          style={{
            background: darkMode.isDark ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => setShowSaveModal(false)}
        >
          <div 
            className="relative w-full max-w-sm rounded-2xl shadow-2xl p-6"
            style={{ background: darkMode.isDark ? '#2D4354' : 'white' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary }}>
              Save Gift
            </h3>
            <p className="text-sm mb-6" style={{ color: darkMode.textSecondary }}>
              Where would you like to save this gift?
            </p>

            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setShowRecipientSelector(true);
                }}
                data-testid="save-modal-keepsake-option"
                className="w-full p-4 rounded-xl flex items-center gap-3 transition-all hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(135deg, #FED7A5 0%, #E8C795 100%)',
                  boxShadow: '0 4px 12px rgba(254, 215, 165, 0.3)'
                }}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.9)' }}>
                  <User className="w-5 h-5" style={{ color: '#9E6752' }} />
                </div>
                <div className="text-left flex-1">
                  <div className="font-semibold" style={{ color: '#20212B' }}>Save for Someone</div>
                  <div className="text-xs" style={{ color: '#534145' }}>Add to a keepsake profile</div>
                </div>
              </button>

              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setShowWishlistSelector(true);
                }}
                data-testid="save-modal-wishlist-option"
                className="w-full p-4 rounded-xl flex items-center gap-3 transition-all hover:scale-[1.02]"
                style={{
                  background: 'linear-gradient(135deg, #E8B4B8 0%, #D4A4A8 100%)',
                  boxShadow: '0 4px 12px rgba(232, 180, 184, 0.3)'
                }}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.9)' }}>
                  <Heart className="w-5 h-5" style={{ color: '#9E6752' }} />
                </div>
                <div className="text-left flex-1">
                  <div className="font-semibold" style={{ color: '#20212B' }}>My Wishlists</div>
                  <div className="text-xs" style={{ color: '#534145' }}>Choose a wishlist to save to</div>
                </div>
              </button>

              <button
                onClick={() => setShowSaveModal(false)}
                className="w-full p-3 rounded-xl text-center transition-all hover:bg-opacity-80"
                style={{ 
                  background: darkMode.isDark ? 'rgba(255,255,255,0.1)' : '#F3F4F6',
                  color: darkMode.textPrimary 
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}